/** @jsxImportSource @emotion/react */
import './MbFormItem.scss';
import { css } from '@emotion/react';
import type { InputBaseProps, SelectProps, TextFieldProps } from '@mui/material';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DesktopDatePicker, DesktopTimePicker } from '@mui/x-date-pickers';
import type { Control } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import type { MbFormFieldType, MbSelectProps } from './type';

type MbFormItemProps = {
  startCol?: number;
  endCol?: number;
  control: Control;
  size?: Pick<InputBaseProps, 'size'>['size'];
  render?: React.ReactNode;
} & MbFormFieldType;

export const MbFormItem = (props: MbFormItemProps) => {
  const {
    type,
    itemProps = {},
    layout,
    name,
    hidden,
    render,
    startCol,
    endCol,
    control,
    size,
    label,
    after,
    isRequired,
    onChange,
    onBlur,
    error,
    errorMsg,
    selectNoneText,
    labelSpan = 2,
    inputSpan = 3,
    column = 5,
  } = props;

  let shrink = true;

  const newLabel = isRequired ? `${label}*` : label;

  if (label) {
    itemProps.label = newLabel;
  }

  if (layout === 'horizontal') {
    if (type === 'select') {
      itemProps.label = undefined;
    } else {
      itemProps.label = false;
    }
    shrink = false;
  }

  const displayNone = hidden ? { display: 'none' } : {};
  const horizontalStyle = layout === 'horizontal' ? { gridTemplateColumns: `repeat(${column},1fr)` } : {};
  const horizontalItemStyle =
    layout === 'horizontal' ? { gridColumnStart: labelSpan + 1, gridColumnEnd: labelSpan + inputSpan + 1 } : {};

  const cssa = css`
    ::after {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      content: '${after?.content}';
      right: ${after?.right}px;
    }
  `;

  let com = null;
  switch (type) {
    case 'text':
      com = (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TextField
              size={size}
              {...field}
              {...(itemProps as TextFieldProps)}
              style={displayNone}
              InputLabelProps={{ shrink }}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e.target.value);
              }}
              onBlur={(e) => {
                field.onBlur();
                onBlur?.(e.target.value);
              }}
              error={error}
              helperText={errorMsg}
              fullWidth
            />
          )}
        />
      );
      break;

    case 'date':
      com = (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <DesktopDatePicker
              closeOnSelect
              {...(itemProps as React.ComponentProps<typeof DesktopDatePicker>)}
              {...field}
              onChange={(e) => {
                const date = e?.format('YYYY-MM-DD') || '';
                field.onChange(e);
                onChange?.(date);
              }}
              slots={{
                toolbar: undefined,
                ...((itemProps as React.ComponentProps<typeof DesktopDatePicker>).slots || {}),
              }}
              slotProps={{
                textField: {
                  size,
                  InputLabelProps: { shrink: true },
                  onKeyDown: (e) => {
                    if (e.key !== 'Tab') {
                      e.preventDefault();
                    }
                  },
                },
                calendarHeader: { format: 'YYYY年MM月' },
                actionBar: { actions: [] },
                ...((itemProps as React.ComponentProps<typeof DesktopDatePicker>).slotProps || {}),
              }}
            />
          )}
        />
      );
      break;

    case 'time':
      com = (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <DesktopTimePicker
              timeSteps={{ minutes: 1 }}
              closeOnSelect
              {...(itemProps as React.ComponentProps<typeof DesktopTimePicker>)}
              {...field}
              onChange={(e) => {
                const time = e?.format('HH:mm') || '00:00';
                field.onChange(e);
                onChange?.(time);
              }}
              slots={{
                toolbar: undefined,
                ...((itemProps as React.ComponentProps<typeof DesktopTimePicker>).slots || {}),
              }}
              slotProps={{
                textField: {
                  size,
                  ...(itemProps as TextFieldProps),
                  InputLabelProps: { shrink: true },
                  onKeyDown: (e) => {
                    if (e.key !== 'Tab') {
                      e.preventDefault();
                    }
                  },
                },
                actionBar: { actions: [] },
                ...((itemProps as React.ComponentProps<typeof DesktopTimePicker>).slotProps || {}),
              }}
            />
          )}
        />
      );
      break;

    case 'password':
      com = (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TextField
              size={size}
              type="password"
              {...field}
              {...(itemProps as TextFieldProps)}
              style={displayNone}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e.target.value);
              }}
              onBlur={(e) => {
                field.onBlur();
                onBlur?.(e.target.value);
              }}
              error={error}
              helperText={errorMsg}
              fullWidth
            />
          )}
        />
      );
      break;
    case 'number':
      com = (
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TextField
              size={size}
              type="number"
              {...field}
              {...(itemProps as TextFieldProps)}
              style={displayNone}
              onChange={(e) => {
                field.onChange(e);
                onChange?.(e.target.value);
              }}
              onBlur={(e) => {
                field.onBlur();
                onBlur?.(e.target.value);
              }}
              error={error}
              helperText={errorMsg}
              fullWidth
            />
          )}
        />
      );
      break;

    case 'select':
      com = (
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const { choices } = itemProps as MbSelectProps;

            return (
              <FormControl>
                {label && layout !== 'horizontal' && <InputLabel shrink={shrink}>{label}</InputLabel>}
                <Select
                  size={size}
                  {...field}
                  {...(itemProps as SelectProps)}
                  style={displayNone}
                  onChange={(e) => {
                    field.onChange(e);
                    onChange?.(e.target.value as string);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    onBlur?.(e.target.value);
                  }}
                  error={error}
                  displayEmpty
                  required={isRequired}
                  fullWidth
                >
                  {!isRequired && (
                    <MenuItem value="">
                      <em>{selectNoneText || 'None'}</em>
                    </MenuItem>
                  )}
                  {choices?.map((option) => (
                    <MenuItem value={option.key} key={option.key}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
                {error && <FormHelperText sx={{ color: '#d32f2f' }}>{errorMsg}</FormHelperText>}
              </FormControl>
            );
          }}
        />
      );
      break;

    case 'render':
      com = render;
      break;

    default:
      break;
  }

  return (
    <div
      className="mb-form-item"
      style={{ ...displayNone, gridColumnStart: startCol, gridColumnEnd: endCol, ...horizontalStyle }}
      css={cssa}
    >
      {layout === 'horizontal' && (
        <div className="mb-form-item-label" style={{ gridColumnStart: 1, gridColumnEnd: labelSpan + 1 }}>
          <label htmlFor={name}>
            {label}
            {isRequired && (
              <span className="must-mark">
                <strong>*</strong>
              </span>
            )}
          </label>
        </div>
      )}
      <div style={{ ...horizontalItemStyle, display: 'grid' }}>{com}</div>
    </div>
  );
};
